import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  Clock, 
  Calendar,
  Image as ImageIcon,
  MessageCircle,
  ExternalLink,
  MapPin
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import StageProgress from './StageProgress';
import TimeLogModal from './TimeLogModal';
import EditProductModal from './EditProductModal';
import SharePointManagementModal from './SharePointManagementModal';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { getAbsoluteUrl } from '../utils/urlHelper';
import { formatTimeCompact } from '../utils/timeFormat';

const ProductCard = ({ product, onUpdate, onProductSelect, isSelected }) => {
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSharePointModal, setShowSharePointModal] = useState(false);
  const { toast } = useToast();
  const { hasPermission } = useAuth();

  const priorityConfig = {
    high: { label: 'High', color: 'bg-red-100 text-red-800 border-red-200' },
    medium: { label: 'Medium', color: 'bg-orange-100 text-orange-800 border-orange-200' },
    low: { label: 'Low', color: 'bg-green-100 text-green-800 border-green-200' }
  };

  const stageLabels = {
    design_concept: 'Design Concept',
    design_prototype: 'Design Prototype',
    development: 'Development',
    testing_verification: 'Testing & Verification',
    documentation: 'Documentation',
    sales_approval: 'Sales Approval',
    project: 'Project',
    strategy: 'Strategy',
    supply_chain: 'Supply Chain',
    delivery: 'Delivery',
    release: 'Release'
  };

  const handleDelete = async () => {
    console.log('ProductCard handleDelete called for product:', product.id); // Debug log
    if (window.confirm('Are you sure you want to delete this product?')) {
      console.log('User confirmed deletion, making API call'); // Debug log
      try {
        await api.delete(`/products/${product.id}`);
        console.log('Delete API call successful'); // Debug log
        onUpdate();
        toast({
          title: "Success",
          description: "Product deleted successfully",
        });
      } catch (error) {
        console.log('Delete API call failed:', error); // Debug log
        toast({
          title: "Error",
          description: "Failed to delete product",
          variant: "destructive",
        });
      }
    } else {
      console.log('User cancelled deletion'); // Debug log
    }
  };

  const handleTimeLog = async (timeData) => {
    try {
      await api.post('/time-logs', {
        ...timeData,
        product_id: product.id
      });
      onUpdate();
      setShowTimeModal(false);
      toast({
        title: "Success",
        description: "Time logged successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to log time",
        variant: "destructive",
      });
    }
  };

  const handleUpdate = async (updateData) => {
    try {
      await api.put(`/products/${product.id}`, updateData);
      onUpdate();
      setShowEditModal(false);
      toast({
        title: "Success",
        description: "Product updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update product",
        variant: "destructive",
      });
    }
  };

  

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCardClick = () => {
    if (onProductSelect) {
      onProductSelect(product);
    }
  };

  const handleExpandClick = (e) => {
    e.stopPropagation();
    if (onProductSelect) {
      onProductSelect(product);
    }
  };

  return (
    <>
      <Card 
        className={`card-hover priority-${product.priority} border-0 shadow-lg transition-all duration-300 cursor-pointer hover:shadow-xl hover:scale-105 relative ${
          isSelected ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
        }`}
        onClick={handleCardClick}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={`${priorityConfig[product.priority].color} border`}>
                  {priorityConfig[product.priority].label}
                </Badge>
                <Badge variant="outline" className="text-slate-600">
                  {stageLabels[product.current_stage]}
                </Badge>
              </div>
              <h3 className="font-semibold text-slate-900 text-lg leading-tight line-clamp-2" title={product.title}>
                {product.title}
              </h3>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="h-8 w-8 p-0" 
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation();
                  setShowEditModal(true);
                }}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation();
                  setShowSharePointModal(true);
                }}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  SharePoint
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation();
                  setShowTimeModal(true);
                }}>
                  <Clock className="mr-2 h-4 w-4" />
                  Log Time
                </DropdownMenuItem>
                
                {(() => {
                  const hasDeletePermission = hasPermission('delete_product');
                  console.log('ProductCard delete permission check:', hasDeletePermission, 'for product:', product.id); // Debug log
                  return hasDeletePermission;
                })() && (
                  <DropdownMenuItem onClick={(e) => {
                    console.log('Delete dropdown item clicked'); // Debug log
                    e.stopPropagation();
                    handleDelete();
                  }} className="text-red-600">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Product Image */}
          {product.image_url ? (
            <div className="relative overflow-hidden rounded-lg bg-slate-100">
              <img
                src={getAbsoluteUrl(product.image_url)}
                alt={product.title}
                className="w-full h-32 object-cover"
              />
              {/* Image count badge */}
              {product.image_count > 1 && (
                <Badge className="absolute top-2 right-2 bg-black/70 text-white hover:bg-black/80">
                  <ImageIcon className="w-3 h-3 mr-1" />
                  +{product.image_count - 1}
                </Badge>
              )}
            </div>
          ) : (
            <div className="h-32 bg-slate-100 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-slate-400" />
            </div>
          )}

          {/* Description */}
          <p className="text-slate-600 text-sm line-clamp-2">
            {product.description}
          </p>

          {/* Current Location Display */}
          {product.location && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">Location</p>
                <p className="text-sm text-blue-700">{product.location}</p>
              </div>
            </div>
          )}
          
                    
          {/* Expand Button - DIRECT TEST */}
          <button 
            onClick={handleCardClick}
            className="w-full mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            📋 View Timeline & Notes
          </button>

          {/* Stage Progress */}
          <StageProgress 
            product={product} 
            onUpdate={onUpdate}
          />

          {/* Footer Info */}
          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>{formatDate(product.created_at)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>{formatTimeCompact(product.total_time_logged || 0)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <TimeLogModal
        isOpen={showTimeModal}
        onClose={() => setShowTimeModal(false)}
        onSubmit={handleTimeLog}
        productTitle={product.title}
      />

      <EditProductModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdate}
        product={product}
      />

      <SharePointManagementModal
        product={product}
        isOpen={showSharePointModal}
        onClose={() => setShowSharePointModal(false)}
        onUpdate={onUpdate}
      />

          </>
  );
};

export default ProductCard;